import { Controller } from "@hotwired/stimulus"

{/* 
<article class="fade-in-up">...</article>
<article class="fade-in-left">...</article>
<article class="fade-in-right">...</article>
<article class="fade-in-scale">...</article> 
<article class="fade-in-width" style="width: 75%">...</article>
*/}

// Staggers a class onto observed items as they enter the viewport.
export default class extends Controller {
  static targets = ["item"]

  static values = {
    delay: { type: Number, default: 100 },
    once: { type: Boolean, default: false },
    selector: { type: String, default: ".staggered-fade-in, .fade-in-up, .fade-in-left, .fade-in-right, .fade-in-scale, .fade-in-width" },
    showClass: { type: String, default: "show" },
    startOffset: { type: Number, default: 20 },
    threshold: { type: Number, default: 0 }
  }

  connect() {
    this.queue = []
    this.queuedItems = new Set()
    this.visibleItems = new Set()
    this.animatedItems = new Set()
    this.queueTimeout = null
    this.observedItems = this.items
    this.prepareWidthAnimations()
    this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
      rootMargin: this.rootMargin,
      threshold: this.thresholdValue
    })

    this.observedItems.forEach((item) => this.observer.observe(item))
  }

  disconnect() {
    this.clearQueueTimeout()
    this.observer?.disconnect()
  }

  handleIntersect(entries) {
    entries
      .filter((entry) => !entry.isIntersecting)
      .forEach((entry) => {
        this.visibleItems.delete(entry.target)
        this.dequeue(entry.target)
        if (!this.onceValue) this.hideItem(entry.target)
      })

    const enteringItems = entries
      .filter((entry) => entry.isIntersecting)
      .sort((firstEntry, secondEntry) => this.itemIndex(firstEntry.target) - this.itemIndex(secondEntry.target))
      .map((entry) => entry.target)

    enteringItems.forEach((item) => {
      this.visibleItems.add(item)
      this.enqueue(item)
    })

    this.sortQueue()
    this.processQueue()
  }

  enqueue(item) {
    if ((this.onceValue && this.animatedItems.has(item)) || this.queuedItems.has(item)) return

    this.queue.push(item)
    this.queuedItems.add(item)
  }

  dequeue(item) {
    if (!this.queuedItems.has(item)) return

    this.queue = this.queue.filter((queuedItem) => queuedItem !== item)
    this.queuedItems.delete(item)
  }

  processQueue() {
    if (this.queueTimeout || this.queue.length === 0) return

    const item = this.queue.shift()
    this.queuedItems.delete(item)

    if (this.visibleItems.has(item)) {
      this.showItem(item)
      this.animatedItems.add(item)

      if (this.onceValue) this.observer.unobserve(item)
    }

    this.queueTimeout = window.setTimeout(() => {
      this.queueTimeout = null
      this.processQueue()
    }, this.delayValue)
  }

  clearQueueTimeout() {
    if (!this.queueTimeout) return

    clearTimeout(this.queueTimeout)
    this.queueTimeout = null
  }

  prepareWidthAnimations() {
    this.widthItems.forEach((item) => {
      const targetWidth = item.dataset.staggeredFadeInWidth || item.style.width
      if (!targetWidth) return

      item.dataset.staggeredFadeInTargetWidth = targetWidth
      item.style.width = "0%"
    })
  }

  showItem(item) {
    item.classList.add(this.showClassValue)

    if (this.isWidthItem(item)) {
      item.style.width = item.dataset.staggeredFadeInTargetWidth
    }
  }

  hideItem(item) {
    item.classList.remove(this.showClassValue)

    if (this.isWidthItem(item)) {
      item.style.width = "0%"
    }
  }

  sortQueue() {
    this.queue.sort((firstItem, secondItem) => this.itemIndex(firstItem) - this.itemIndex(secondItem))
  }

  get items() {
    if (this.hasItemTarget) return this.itemTargets

    return Array.from(this.element.querySelectorAll(this.selectorValue))
  }

  get widthItems() {
    return this.observedItems.filter((item) => this.isWidthItem(item))
  }

  isWidthItem(item) {
    return item.classList.contains("fade-in-width")
  }

  itemIndex(item) {
    return this.observedItems.indexOf(item)
  }

  get rootMargin() {
    const offset = Math.min(Math.max(this.startOffsetValue, 0), 99)

    return `0px 0px -${offset}% 0px`
  }
}
